import { motion } from "framer-motion";
import { BookOpen, DivideIcon as LucideIcon, Shield, Sword, Wand } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
}
interface WhyUseReason {
  icon: React.ReactNode;
  title: string;
  description: string;
}
interface HowToStep {
  title: string;
  description: string;
}
interface PromptTip {
  icon: React.ReactNode;
  title: string;
  description: string;
}
interface ExamplePrompt {
  icon: React.ReactNode;
  class: string;
  description: string;
}
interface GalleryImage {
  src: string;
  title: string;
  description: string;
  link: string;
}
interface LandingPageProps {
  title: string;
  subtitle: string;
  mainIcon?: typeof LucideIcon;
  features: Feature[];
  whyUseTitle: string;
  whyUseSubtitle: string;
  whyUseReasons: WhyUseReason[];
  howToTitle: string;
  howToSubtitle: string;
  howToSteps: HowToStep[];
  promptTipsTitle: string;
  promptTipsSubtitle: string;
  promptTips: PromptTip[];
  examplePromptsTitle: string;
  examplePromptsSubtitle: string;
  examplePrompts: ExamplePrompt[];
  galleryImages: GalleryImage[];
  ctaTitle: string;
  ctaSubtitle: string;
  ctaButtonText: string;
  finalCtaTitle: string;
  finalCtaSubtitle: string;
}
const CTASection = ({
  title,
  subtitle,
  buttonText
}: {
  title: string;
  subtitle: string;
  buttonText: string;
}) => <section className="py-16 bg-gradient-to-r from-slate-900 via-sky-900/10 to-slate-900" data-sentry-component="CTASection" data-sentry-source-file="index.tsx">
        <div className="container mx-auto px-4 text-center">
            <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8
    }} className="max-w-2xl mx-auto" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                <h2 className="text-3xl font-bold mb-4">{title}</h2>
                <p className="text-lg text-slate-300 mb-8">{subtitle}</p>
                <Link title="Generate DnD Art" href="/generate" data-sentry-element="Link" data-sentry-source-file="index.tsx">
                    <motion.button whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} className="px-8 py-4 bg-gradient-to-r from-sky-500 to-sky-600 rounded-lg font-bold text-lg shadow-lg hover:shadow-sky-500/20 transition-all duration-300" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        {buttonText}
                    </motion.button>
                </Link>
            </motion.div>
        </div>
    </section>;
export default function LandingPage({
  title,
  subtitle,
  mainIcon: MainIcon = Wand,
  features,
  whyUseTitle,
  whyUseSubtitle,
  whyUseReasons,
  howToTitle,
  howToSubtitle,
  howToSteps,
  promptTipsTitle,
  promptTipsSubtitle,
  promptTips,
  examplePromptsTitle,
  examplePromptsSubtitle,
  examplePrompts,
  galleryImages,
  ctaTitle,
  ctaSubtitle,
  ctaButtonText,
  finalCtaTitle,
  finalCtaSubtitle
}: LandingPageProps) {
  return <div className="min-h-screen bg-slate-950 text-slate-100" suppressHydrationWarning data-sentry-component="LandingPage" data-sentry-source-file="index.tsx">
            {/* Hero Section */}
            <section className="relative min-h-[90vh] flex flex-col items-center justify-center py-32 px-4 bg-gradient-to-b from-slate-900 via-purple-950/20 to-slate-950 overflow-hidden">
                {/* Animated Background Elements */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute w-full h-full bg-[radial-gradient(circle_500px_at_50%_50%,rgba(56,189,248,0.15),transparent)]" />
                    {[...Array(20)].map((_, i) => <motion.div key={i} className="absolute w-1 h-1 bg-purple-400/30 rounded-full" style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`
        }} animate={{
          scale: [1, 2, 1],
          opacity: [0.3, 0.8, 0.3]
        }} transition={{
          duration: 3 + Math.random() * 2,
          repeat: Infinity,
          delay: Math.random() * 2
        }} />)}
                </div>

                <motion.div initial={{
        opacity: 0,
        y: -50
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1
      }} className="relative z-10 w-full max-w-4xl mx-auto flex flex-col items-center" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <div className="flex flex-col items-center justify-center gap-4 mb-6">
                        <motion.div animate={{
            rotate: [0, 5, -5, 0],
            scale: [1, 1.1, 1]
          }} transition={{
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut"
          }} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                            <MainIcon className="w-16 h-16 text-green-400 mb-4" data-sentry-element="MainIcon" data-sentry-source-file="index.tsx" />
                        </motion.div>
                        <div className="relative">
                            <motion.h1 className="text-4xl sm:text-5xl md:text-8xl lg:text-9xl font-extrabold bg-gradient-to-r from-sky-400 via-sky-500 to-sky-400 bg-clip-text text-transparent bg-[size:200%] text-center px-2" animate={{
              backgroundPosition: ["0%", "100%", "0%"]
            }} transition={{
              duration: 10,
              repeat: Infinity,
              ease: "linear"
            }} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                {title}
                            </motion.h1>
                            <motion.div className="absolute -bottom-4 left-0 right-0 h-1 bg-gradient-to-r from-sky-400 to-sky-500 rounded-full" initial={{
              scaleX: 0,
              opacity: 0
            }} animate={{
              scaleX: 1,
              opacity: 1
            }} transition={{
              delay: 0.5,
              duration: 1
            }} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                        </div>
                    </div>
                    <motion.p className="text-lg sm:text-xl md:text-2xl max-w-2xl text-center text-slate-300 mb-10 px-4" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.8
        }} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        {subtitle}
                    </motion.p>
                    <motion.div className="flex justify-center gap-4" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 1
        }} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        <Link title="Generate Fantasy Art" href="/generate" data-sentry-element="Link" data-sentry-source-file="index.tsx">
                            <motion.button whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="relative px-8 py-4 bg-gradient-to-r from-sky-500 to-sky-600 rounded-lg font-bold text-lg shadow-lg group overflow-hidden" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                <div className="absolute inset-0 bg-gradient-to-r from-sky-600 to-sky-700 opacity-0 group-hover:opacity-100 transition-opacity" />
                                <span className="relative z-10">
                                    Start Creating
                                </span>
                            </motion.button>
                        </Link>
                        <Link title="View Gallery" href="/gallery" data-sentry-element="Link" data-sentry-source-file="index.tsx">
                            <motion.button whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="relative px-8 py-4 bg-slate-800 rounded-lg font-bold text-lg shadow-lg group overflow-hidden" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                <div className="absolute inset-0 bg-slate-700 opacity-0 group-hover:opacity-100 transition-opacity" />
                                <span className="relative z-10">
                                    View Gallery
                                </span>
                            </motion.button>
                        </Link>
                    </motion.div>
                </motion.div>

                {/* Floating Elements */}
                <motion.div animate={{
        y: [0, -20, 0],
        rotate: [0, 5, -5, 0]
      }} transition={{
        duration: 5,
        repeat: Infinity,
        ease: "linear"
      }} className="absolute top-1/4 left-1/4 opacity-20" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Sword className="w-24 h-24" data-sentry-element="Sword" data-sentry-source-file="index.tsx" />
                </motion.div>
                <motion.div animate={{
        y: [0, 20, 0],
        rotate: [0, -5, 5, 0]
      }} transition={{
        duration: 6,
        repeat: Infinity,
        ease: "linear"
      }} className="absolute bottom-1/4 right-1/4 opacity-20" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Shield className="w-24 h-24" data-sentry-element="Shield" data-sentry-source-file="index.tsx" />
                </motion.div>
                <motion.div animate={{
        y: [0, 15, 0],
        rotate: [0, 10, -10, 0]
      }} transition={{
        duration: 7,
        repeat: Infinity,
        ease: "linear"
      }} className="absolute top-1/3 right-1/3 opacity-20" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <BookOpen className="w-24 h-24" data-sentry-element="BookOpen" data-sentry-source-file="index.tsx" />
                </motion.div>
            </section>

            {/* Features Section */}
            <section className="py-24 bg-slate-900">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-16">
                        <motion.h2 className="text-3xl md:text-4xl font-bold inline-block relative pb-4" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                            Create Art for Your Adventures
                            <motion.div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-sky-400 to-sky-500" initial={{
              scaleX: 0
            }} whileInView={{
              scaleX: 1
            }} transition={{
              duration: 0.8,
              delay: 0.2
            }} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                        </motion.h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                        {features.map((feature, index) => <motion.div key={feature.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.2,
            duration: 0.6
          }} whileHover={{
            y: -5
          }} className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 hover:bg-slate-800/80 transition-all duration-300 border border-slate-700/50">
                                <motion.div className="w-12 h-12 rounded-lg bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center mb-4" whileHover={{
              rotate: 5,
              scale: 1.1
            }} transition={{
              type: "spring",
              stiffness: 300
            }}>
                                    {feature.icon}
                                </motion.div>
                                <h3 className="text-xl font-bold mb-2">
                                    {feature.title}
                                </h3>
                                <p className="text-slate-400">
                                    {feature.description}
                                </p>
                            </motion.div>)}
                    </div>
                </div>
            </section>

            {/* Gallery Preview */}
            <section className="py-24 bg-slate-950">
                <div className="container mx-auto px-4">
                    <motion.h2 className="text-4xl font-bold text-center mb-16 relative inline-block" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        Stunning Creations
                        <motion.div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-sky-400 to-sky-500" initial={{
            scaleX: 0
          }} whileInView={{
            scaleX: 1
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                    </motion.h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8 max-w-6xl mx-auto">
                        {galleryImages.map((image, index) => <motion.div key={index} initial={{
            opacity: 0,
            scale: 0.8
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: index * 0.1
          }} className="relative group">
                                <div className="relative aspect-square rounded-xl overflow-hidden bg-slate-800">
                                    <Image src={image.src} alt={`${image.title} - D&D Character Art`} fill className="object-cover transition-transform duration-500 group-hover:scale-110" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/20 to-transparent opacity-60 transition-opacity group-hover:opacity-80" />

                                    <div className="absolute inset-0 flex flex-col justify-end p-6">
                                        <h3 className="text-2xl font-bold text-white mb-2">
                                            {image.title}
                                        </h3>
                                        <p className="text-slate-300 mb-4 line-clamp-2">
                                            {image.description}
                                        </p>
                                        <Link href={image.link}>
                                            <motion.button whileHover={{
                    scale: 1.05
                  }} whileTap={{
                    scale: 0.95
                  }} className="w-full px-4 py-2 bg-sky-500/20 hover:bg-sky-500/30 border border-sky-500/50 rounded-lg font-semibold text-white backdrop-blur-sm transition-all duration-300">
                                                Generate
                                            </motion.button>
                                        </Link>
                                    </div>
                                </div>
                            </motion.div>)}
                    </div>
                </div>
            </section>

            {/* Why Use Section */}
            <section className="py-24 bg-slate-900">
                <div className="container mx-auto px-4 max-w-4xl">
                    <motion.h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-8 md:mb-12 text-center relative inline-block px-2" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        {whyUseTitle}
                        <motion.div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-sky-400 to-sky-500" initial={{
            scaleX: 0
          }} whileInView={{
            scaleX: 1
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                    </motion.h2>
                    <motion.p className="text-slate-400 mb-12 text-center text-lg" initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        {whyUseSubtitle}
                    </motion.p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {whyUseReasons.map((reason, index) => <motion.div key={reason.title} className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 hover:bg-slate-800/80 transition-all duration-300" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} whileHover={{
            y: -5
          }} transition={{
            delay: index * 0.1,
            duration: 0.3
          }}>
                                <div className="flex items-center gap-4 mb-4">
                                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center">
                                        {reason.icon}
                                    </div>
                                    <h3 className="text-xl font-bold text-white">
                                        {reason.title}
                                    </h3>
                                </div>
                                <p className="text-slate-300">
                                    {reason.description}
                                </p>
                            </motion.div>)}
                    </div>
                </div>
            </section>

            <CTASection title={ctaTitle} subtitle={ctaSubtitle} buttonText={ctaButtonText} data-sentry-element="CTASection" data-sentry-source-file="index.tsx" />

            {/* How To Use Section */}
            <section className="py-24 bg-slate-800">
                <div className="container mx-auto px-4 max-w-3xl">
                    <motion.h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center relative inline-block" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        {howToTitle}
                        <motion.div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-sky-400 to-sky-500" initial={{
            scaleX: 0
          }} whileInView={{
            scaleX: 1
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                    </motion.h2>
                    <motion.p className="text-slate-400 mb-12 text-center text-lg" initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        {howToSubtitle}
                    </motion.p>
                    <div className="relative">
                        <div className="absolute left-[1.85rem] sm:left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-sky-500 via-sky-600 to-sky-500" />
                        {howToSteps.map((step, index) => <motion.li key={index} className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 mb-8 relative" initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} whileHover={{
            x: 5
          }} transition={{
            delay: index * 0.1,
            duration: 0.3
          }}>
                                <span className="flex-shrink-0 w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center text-white text-xl font-bold shadow-lg">
                                    {index + 1}
                                </span>
                                <div className="bg-slate-700/50 rounded-xl p-6 flex-1 backdrop-blur-sm border border-slate-600/50">
                                    <h3 className="text-xl font-bold text-white mb-2">
                                        {step.title}
                                    </h3>
                                    <p className="text-slate-300">
                                        {step.description}
                                    </p>
                                </div>
                            </motion.li>)}
                    </div>
                </div>
            </section>

            {/* Writing Prompts Section */}
            <section className="py-24 bg-slate-900">
                <div className="container mx-auto px-4 max-w-3xl">
                    <motion.h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center relative inline-block" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        {promptTipsTitle}
                        <motion.div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-sky-400 to-sky-500" initial={{
            scaleX: 0
          }} whileInView={{
            scaleX: 1
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                    </motion.h2>
                    <motion.p className="text-slate-400 mb-12 text-center text-lg" initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        {promptTipsSubtitle}
                    </motion.p>
                    <div className="grid gap-6">
                        {promptTips.map((tip, index) => <motion.div key={index} className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-xl border border-slate-700/50 hover:bg-slate-800/80 transition-all duration-300" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.1
          }}>
                                <div className="flex items-center gap-4 mb-4">
                                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center">
                                        {tip.icon}
                                    </div>
                                    <h3 className="text-xl font-bold text-white">
                                        {tip.title}
                                    </h3>
                                </div>
                                <p className="text-slate-300">
                                    {tip.description}
                                </p>
                            </motion.div>)}
                    </div>
                </div>
            </section>

            <CTASection title={ctaTitle} subtitle={ctaSubtitle} buttonText={ctaButtonText} data-sentry-element="CTASection" data-sentry-source-file="index.tsx" />

            {/* Example Prompts Section */}
            <section className="py-24 bg-slate-800">
                <div className="container mx-auto px-4 max-w-3xl">
                    <motion.h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center relative inline-block" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        {examplePromptsTitle}
                        <motion.div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-sky-400 to-sky-500" initial={{
            scaleX: 0
          }} whileInView={{
            scaleX: 1
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                    </motion.h2>
                    <motion.p className="text-slate-400 mb-12 text-center text-lg" initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        {examplePromptsSubtitle}
                    </motion.p>
                    <div className="grid gap-6">
                        {examplePrompts.map((prompt, index) => <motion.div key={index} className="bg-slate-900/50 backdrop-blur-sm p-6 rounded-xl border border-slate-700/50 hover:bg-slate-800/80 transition-all duration-300" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} whileHover={{
            y: -5
          }} transition={{
            delay: index * 0.1
          }}>
                                <div className="flex items-center gap-4 mb-4">
                                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center">
                                        {prompt.icon}
                                    </div>
                                    <h3 className="text-xl font-bold text-sky-400">
                                        {prompt.class}
                                    </h3>
                                </div>
                                <p className="text-slate-300">
                                    {prompt.description}
                                </p>
                            </motion.div>)}
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="py-24 bg-gradient-to-b from-slate-900 to-slate-950">
                <div className="container mx-auto px-4 text-center">
                    <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        <h2 className="text-4xl font-bold mb-6">
                            {finalCtaTitle}
                        </h2>
                        <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
                            {finalCtaSubtitle}
                        </p>
                        <Link title="Start Creating" href="/generate" data-sentry-element="Link" data-sentry-source-file="index.tsx">
                            <motion.button whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="px-8 py-4 bg-gradient-to-r from-sky-500 to-sky-600 rounded-lg font-bold text-lg shadow-lg hover:shadow-sky-500/20 transition-shadow" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                Start Creating Now
                            </motion.button>
                        </Link>
                    </motion.div>
                </div>
            </section>
        </div>;
}